import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import BEM from '../../bem';
import mobxInjectSelect from '../../utils/mobxInjectSelect';
import tzTime from '../../utils/tzTime';
import { taskActionEvent } from '../../utils/taskTracking';
import type { LogGainsightAnalytics } from '../../utils/taskTracking';
import {
  buildTaskDescription,
  buildTaskTitle,
  isFutureDateTime,
  toDatetimeLocal,
  toDueAtUtc,
} from '../../utils/taskPrefill';
import Modal from '../Modal';
import PrioritySelect from '../PrioritySelect/PrioritySelect';
import { useAppSelector } from 'redux-stores';
import { useCreateTaskMutation } from 'redux-stores/tasks';
import type { CreateTaskPayload, TaskPriority } from 'js-sdk/src/types/Tasks';

const classes = BEM.with('CreateTaskModal');

const MAX_TITLE_CHARACTERS = 255;

type LinkedMessage = {
  id: string;
  serverId?: string;
  body: string;
  createdAt: string | Date;
  sender?: { displayName?: string };
};

type FormValues = {
  title: string;
  description: string;
  priority: TaskPriority;
  reminderEnabled: boolean;
  dueAt: string;
};

type CreateTaskModalProps = {
  isOpen: boolean;
  options: {
    message?: LinkedMessage;
  };
};

type MobxProps = {
  closeModal: () => void;
  currentOrganizationId: string;
  currentUserId: string;
  logGainsightAnalytics: LogGainsightAnalytics;
};

function CreateTaskModal({
  closeModal,
  currentOrganizationId,
  currentUserId,
  logGainsightAnalytics,
  isOpen,
  options,
}: CreateTaskModalProps & MobxProps) {
  const message = options?.message;
  const messageId = message ? message.serverId || message.id : '';

  const [createTask, { isLoading }] = useCreateTaskMutation();
  const accessibilityMode = useAppSelector((state) => state.ui.accessibilityMode);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      title: message ? buildTaskTitle(message.body) : '',
      description: message ? buildTaskDescription(message.body) : '',
      priority: 'medium',
      reminderEnabled: false,
      dueAt: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        title: message ? buildTaskTitle(message.body) : '',
        description: message ? buildTaskDescription(message.body) : '',
        priority: 'medium',
        reminderEnabled: false,
        dueAt: '',
      });
    } else {
      reset({
        title: '',
        description: '',
        priority: 'medium',
        reminderEnabled: false,
        dueAt: '',
      });
      setSubmitError(null);
    }
  }, [isOpen, message, reset]);

  const title = watch('title').trim();

  const linkedMessageTime = useMemo(
    () => (message ? tzTime(message.createdAt).format('h:mm A') : ''),
    [message]
  );

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);

    const payload: CreateTaskPayload = {
      title: values.title.trim(),
      description: values.description.trim() ? values.description.trim() : null,
      priority: values.priority,
      assignee_id: currentUserId,
      due_at: toDueAtUtc(values.dueAt),
      reminder_enabled: values.reminderEnabled,
      ...(message
        ? { source_type: 'message', source_ref: { message_id: messageId } }
        : { source_type: 'manual' }),
    };

    try {
      const task = await createTask({ organizationId: currentOrganizationId, payload }).unwrap();
      logGainsightAnalytics(
        taskActionEvent('Create', currentOrganizationId, currentUserId, task.id)
      );
      closeModal();
    } catch (error) {
      const reason =
        error && typeof error === 'object' && 'reason' in error
          ? String((error as { reason: unknown }).reason)
          : 'Something went wrong.';
      setSubmitError(`Could not create the task. ${reason} Please try again.`);
    }
  };

  return (
    <Modal
      ariaLabelCloseButton="Create Task Close"
      ariaLabelHeader="Create New Task"
      className={classes()}
      header="Create New Task"
      headerClass={classes('header')}
      isOpen={isOpen}
      onRequestClose={closeModal}
      size="medium"
      footerPrimaryActions={
        <button
          aria-label="Create Task"
          className={classes('create-btn', { disabled: isLoading || !title })}
          data-test-id="create-task-submit"
          disabled={isLoading || !title}
          onClick={handleSubmit(onSubmit)}
          type="button"
        >
          {isLoading ? 'Creating...' : 'Create Task'}
        </button>
      }
      footerSecondaryActions={
        <button
          aria-label="Create Task Cancel"
          className={classes('cancel-btn')}
          onClick={closeModal}
          type="button"
        >
          Cancel
        </button>
      }
    >
      <div className={classes('body')}>
        <div className={classes('section')}>
          <label className={classes('label')} htmlFor="create-task-title">
            Task Title <span aria-hidden="true">*</span>
          </label>
          <Controller
            control={control}
            name="title"
            rules={{ required: 'Task title is required.' }}
            render={({ field }) => (
              <input
                {...field}
                aria-describedby={errors.title ? 'create-task-title-error' : undefined}
                aria-invalid={!!errors.title}
                aria-required="true"
                className={classes('title-input')}
                data-test-id="create-task-title"
                id="create-task-title"
                maxLength={MAX_TITLE_CHARACTERS}
                type="text"
              />
            )}
          />
          {errors.title && (
            <div className={classes('field-error')} id="create-task-title-error" role="alert">
              {errors.title.message}
            </div>
          )}
        </div>

        <div className={classes('section')}>
          <label className={classes('label')} htmlFor="create-task-description">
            Description &amp; Notes
          </label>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <textarea
                {...field}
                className={classes('description-input')}
                data-test-id="create-task-description"
                id="create-task-description"
                rows={4}
              />
            )}
          />
        </div>

        <div className={classes('row')}>
          <div className={classes('section', { half: true })}>
            <span className={classes('label')} id="create-task-priority-label">
              Priority
            </span>
            <Controller
              control={control}
              name="priority"
              render={({ field }) => (
                <PrioritySelect
                  value={field.value}
                  onChange={field.onChange}
                  accessibilityMode={accessibilityMode}
                  buttonTestId="create-task-priority"
                  buttonAriaLabelledBy="create-task-priority-label"
                />
              )}
            />
          </div>
        </div>

        <div className={classes('section')}>
          <label className={classes('label')} htmlFor="create-task-due">
            Due
          </label>
          <Controller
            control={control}
            name="dueAt"
            rules={{
              validate: (v) =>
                v === '' || isFutureDateTime(v) || 'Due date & time must be in the future.',
            }}
            render={({ field }) => (
              <input
                {...field}
                aria-describedby={errors.dueAt ? 'create-task-due-error' : undefined}
                aria-invalid={!!errors.dueAt}
                className={classes('due-input', { error: !!errors.dueAt })}
                data-test-id="create-task-due"
                id="create-task-due"
                min={toDatetimeLocal(new Date().toISOString())}
                type="datetime-local"
              />
            )}
          />
          {errors.dueAt && (
            <div className={classes('field-error')} id="create-task-due-error" role="alert">
              {errors.dueAt.message}
            </div>
          )}
        </div>

        {message ? (
          <div className={classes('linked-message')} data-test-id="create-task-linked-message">
            <div className={classes('linked-message-label')}>Linked Message</div>
            <div className={classes('linked-message-body')}>&quot;{message.body}&quot;</div>
            <div className={classes('linked-message-meta')}>
              From {message.sender?.displayName || 'Unknown'} &bull; {linkedMessageTime}
            </div>
          </div>
        ) : null}

        {submitError && (
          <div className={classes('submit-error')} data-test-id="create-task-error" role="alert">
            {submitError}
          </div>
        )}
      </div>
    </Modal>
  );
}

export { CreateTaskModal as CreateTaskModalComponent };

export default mobxInjectSelect<CreateTaskModalProps, MobxProps>({
  messengerStore: ['currentOrganizationId'],
  sessionStore: ['currentUserId'],
  trackerStore: ['logGainsightAnalytics'],
})(CreateTaskModal);
